import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { client } from "@/hono";
import { useCitadel } from "./use-citadel";

/**
 * The permission form of one user: their carried roles, their individual grants, and the
 * grants their roles cover. One hook rather than two inline copies of the query, so the roles
 * table and the permission grid — which invalidate each other's reads on every write — can
 * never drift apart on the key.
 *
 * Behind the same rule its endpoint is gated on, for the reason `useRoles` gives: a colleague's
 * permission sheet is not a page every member may open, and a query that fires before the rules
 * are known answers with a toast rather than with a screen.
 */
export function useUserPermissionsForm(userId: string) {
	/** Hooks */
	const { citadel } = useCitadel();

	/** Query */
	const query = useQuery({
		queryKey: ["user-permissions-form", userId],
		queryFn: async () => {
			const response = await client.users[":userId"].permissions.form.$get({
				param: { userId },
			});
			const data = await responseHandler(response);
			return data.form;
		},
		enabled: citadel?.can("manage", "Permission") === true,
	});

	/** Return */
	return { ...query, form: query.data };
}
